import React from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import type { VariantEditorViewModel } from '../../contracts/commerce-admin';
import { DescriptionText, PaneSection } from '../blocks';

const inputClass = 'w-full rounded-control border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
const buttonClass = 'inline-flex items-center gap-2 rounded-control border border-border px-3 py-2 text-sm text-foreground hover:bg-muted disabled:opacity-40';

export function VariantSpecificationsEditor({ editor }: { editor: VariantEditorViewModel }) {
  return (
    <PaneSection label="Specifications">
      <DescriptionText>Full technical details, separate from selling-point features. Add a name and value, with an optional group.</DescriptionText>
      {editor.validationErrors.specifications && <p role="alert" className="text-sm text-destructive">{editor.validationErrors.specifications}</p>}
      {editor.value.specifications.length === 0 && <p className="text-sm text-muted-foreground">No specifications added.</p>}
      <div className="space-y-3">
        {editor.value.specifications.map((row, index) => (
          <fieldset key={row.id} className="space-y-3 rounded-control border border-border p-3">
            <legend className="px-1 text-sm font-semibold">Specification {index + 1}</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              {(['group', 'name', 'value'] as const).map(field => {
                const error = editor.validationErrors[`specification.${row.id}.${field}`];
                const id = `specification-${row.id}-${field}`;
                return (
                  <div key={field} className={field === 'value' ? 'sm:col-span-2' : ''}>
                    <label htmlFor={id} className="mb-1 block text-sm font-medium">{field === 'group' ? 'Group (optional)' : field === 'name' ? 'Specification name' : 'Specification value'}</label>
                    {field === 'value' ? (
                      <textarea id={id} rows={3} value={row.value} onChange={event => editor.onUpdateSpecification(row.id, { value: event.target.value })} className={inputClass} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} />
                    ) : (
                      <input id={id} value={row[field]} onChange={event => editor.onUpdateSpecification(row.id, { [field]: event.target.value })} className={inputClass} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} />
                    )}
                    {error && <p id={`${id}-error`} role="alert" className="mt-1 text-sm text-destructive">{error}</p>}
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className={buttonClass} disabled={index === 0} aria-label={`Move specification ${index + 1} up`} onClick={() => editor.onMoveSpecification(row.id, -1)}><ArrowUp className="h-4 w-4" />Up</button>
              <button type="button" className={buttonClass} disabled={index === editor.value.specifications.length - 1} aria-label={`Move specification ${index + 1} down`} onClick={() => editor.onMoveSpecification(row.id, 1)}><ArrowDown className="h-4 w-4" />Down</button>
              <button type="button" className={buttonClass} aria-label={`Remove specification ${index + 1}`} onClick={() => editor.onRemoveSpecification(row.id)}><Trash2 className="h-4 w-4" />Remove</button>
            </div>
          </fieldset>
        ))}
      </div>
      <button type="button" className={buttonClass} disabled={editor.value.specifications.length >= 100} onClick={editor.onAddSpecification}><Plus className="h-4 w-4" />Add specification</button>
    </PaneSection>
  );
}

export function VariantSpecificationsDisplay({ rows, onEdit }: { rows: { group?: string; name: string; value: string }[]; onEdit: () => void }) {
  return (
    <section aria-label="Full specifications" className="px-5 py-4 sm:px-6">
      <div className="mb-3 flex items-center justify-between gap-4">
        <h3 className="text-sm font-semibold text-foreground">Full specifications</h3>
        <button type="button" className={buttonClass} onClick={onEdit}>Edit specifications</button>
      </div>
      {rows.length === 0 ? <DescriptionText>No technical specifications added yet.</DescriptionText> : (
        <dl className="divide-y divide-border">
          {rows.map((row, index) => (
            <div key={index} className="grid gap-2 py-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
              <dt className="break-words text-sm font-medium text-foreground">{row.group && <span className="mb-1 block text-xs font-normal text-muted-foreground">{row.group}</span>}{row.name}</dt>
              <dd className="whitespace-pre-wrap break-words text-sm text-muted-foreground">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}
